/**
 * Training Feature Components
 */

const REST_TIMER_SECONDS = 60;
const RING_RADIUS = 60;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function TrainingComponent(config = {}) {
    const i18n = config.i18n || {};

    return {
        currentWorkoutId: config.workoutId || null,
        exerciseIds: config.exerciseIds || [],
        initializing: true,
        initStatus: i18n.booting || 'Booting System...',
        initProgress: 0,
        isFinishing: false,
        checkedExercises: {},
        totalExercises: config.exerciseIds?.length || 0,
        isPinned: false,

        // Rest Timer state
        restTimerActive: false,
        restSecondsLeft: REST_TIMER_SECONDS,
        restTotalSeconds: REST_TIMER_SECONDS,
        restTimerInterval: null,
        ringCircumference: RING_CIRCUMFERENCE,

        // Completion screen
        showCompletion: false,
        lastCheckedId: null,
        workoutStartTime: null,

        init() {
            this.workoutStartTime = Date.now();

            const storedId = sessionStorage.getItem('training_workout_id');
            if (storedId === this.currentWorkoutId) {
                try {
                    this.checkedExercises = JSON.parse(
                        sessionStorage.getItem('training_checks') || '{}'
                    );
                } catch (e) {
                    this.checkedExercises = {};
                }
            } else {
                this.clearProgress();
                if (this.currentWorkoutId) {
                    sessionStorage.setItem('training_workout_id', this.currentWorkoutId);
                }
            }

            this.runInitSequence();
        },

        async runInitSequence() {
            this.initStatus = i18n.booting || 'Booting...';
            this.initProgress = 20;
            await new Promise((r) => setTimeout(r, 400));

            this.initStatus = i18n.syncing || 'Syncing...';
            this.initProgress = 60;
            await new Promise((r) => setTimeout(r, 400));

            this.initStatus = i18n.calibrating || 'Calibrating...';
            this.initProgress = 100;
            await new Promise((r) => setTimeout(r, 400));

            this.initializing = false;

            if (window.toasts) {
                window.toasts.success(i18n.system_ready || 'System Ready', { duration: 2000 });
            }
        },

        get checkedCount() {
            return Object.values(this.checkedExercises).filter((v) => v).length;
        },

        get percentage() {
            return this.totalExercises > 0 ? (this.checkedCount / this.totalExercises) * 100 : 0;
        },

        get nextUpId() {
            return this.exerciseIds.find((id) => !this.checkedExercises[id]);
        },

        get isComplete() {
            return this.checkedCount === this.totalExercises && this.totalExercises > 0;
        },

        get elapsedMinutes() {
            return Math.floor((Date.now() - this.workoutStartTime) / 60000);
        },

        get ringDashOffset() {
            const progress = this.restSecondsLeft / this.restTotalSeconds;
            return RING_CIRCUMFERENCE * (1 - progress);
        },

        toggleCheck(id) {
            if (this.isFinishing) return;

            const wasChecked = this.checkedExercises[id];
            this.checkedExercises[id] = !wasChecked;
            sessionStorage.setItem('training_checks', JSON.stringify(this.checkedExercises));

            if (!wasChecked) {
                this.lastCheckedId = id;

                // Haptic feedback
                if (window.navigator.vibrate) window.navigator.vibrate([30, 20, 30]);

                // Particle burst at the ritual switch
                this._burstParticles(id);

                // Check if workout is now complete
                if (this.isComplete) {
                    setTimeout(() => {
                        this.showCompletion = true;
                    }, 500);
                    return;
                }

                // Start rest timer between exercises
                this.startRestTimer();

                // Auto-scroll to next exercise after rest timer starts
                setTimeout(() => {
                    const nextId = this.nextUpId;
                    if (nextId) {
                        const target = document.getElementById(`exercise-${nextId}`);
                        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 300);
            } else {
                // Unchecking: clear lastCheckedId if it was this exercise
                if (this.lastCheckedId === id) this.lastCheckedId = null;
            }
        },

        dismissCompletion() {
            this.showCompletion = false;
            if (this.lastCheckedId) {
                this.checkedExercises[this.lastCheckedId] = false;
                sessionStorage.setItem('training_checks', JSON.stringify(this.checkedExercises));
                this.lastCheckedId = null;
            }
        },

        startRestTimer(seconds = REST_TIMER_SECONDS) {
            this.stopRestTimer();
            this.restTotalSeconds = seconds;
            this.restSecondsLeft = seconds;
            this.restTimerActive = true;

            this.restTimerInterval = setInterval(() => {
                this.restSecondsLeft--;
                if (this.restSecondsLeft <= 0) {
                    this.stopRestTimer();
                    if (window.navigator.vibrate) window.navigator.vibrate([50, 100, 50, 100, 50]);
                }
            }, 1000);
        },

        stopRestTimer() {
            if (this.restTimerInterval) {
                clearInterval(this.restTimerInterval);
                this.restTimerInterval = null;
            }
            this.restTimerActive = false;
        },

        skipRestTimer() {
            this.stopRestTimer();
        },

        _burstParticles(exerciseId) {
            const btn = document.querySelector(`#exercise-${exerciseId} .ritual-switch`);
            if (!btn) return;

            const rect = btn.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;

            const colors = [
                'var(--color-brand-secondary-surface)',
                'var(--color-brand-primary-surface)',
                'var(--color-feedback-success-surface)',
            ];

            for (let i = 0; i < 6; i++) {
                const p = document.createElement('div');
                p.className = 'particle fixed';
                const angle = (i / 6) * 2 * Math.PI;
                const distance = 40 + Math.random() * 30;
                p.style.cssText = `
                    left: ${cx - 4}px;
                    top: ${cy - 4}px;
                    background: ${colors[i % colors.length]};
                    --tx: ${Math.cos(angle) * distance}px;
                    --ty: ${Math.sin(angle) * distance}px;
                    z-index: 999;
                `;
                document.body.appendChild(p);
                setTimeout(() => p.remove(), 700);
            }
        },

        clearProgress() {
            this.checkedExercises = {};
            sessionStorage.removeItem('training_checks');
            sessionStorage.removeItem('training_workout_id');
        },
    };
}
